import { EntityType } from "@mapmycustomers/shared/enum";
import { splitStringByRegExp } from "@mapmycustomers/shared/util/stringUtils";

// Server-side marker types are singular and hyphen-delimited.
export type MentionMarkerType =
  | "account-group"
  | "account"
  | "contact-group"
  | "contact"
  | "deal-group"
  | "deal"
  | "territory"
  | "unified-route"
  | "user";

export interface EntityMention {
  displayName: string;
  entityId: number;
  type: MentionMarkerType;
}

// Drives the regex below so it can't drift from MentionMarkerType.
const MENTION_MARKER_TYPES: MentionMarkerType[] = [
  "account-group",
  "account",
  "contact-group",
  "contact",
  "deal-group",
  "deal",
  "territory",
  "unified-route",
  "user",
];

// FE-only marker: the server parses only the entity types above, so a chart
// rides along as plain text and its name comes from ChartMentionsContext.
export const CHART_MENTION_MARKER = "chart";

export const createChartMentionToken = (chartId: number): string =>
  `@${CHART_MENTION_MARKER}:${chartId}`;

export const hasChartMention = (content: string): boolean =>
  new RegExp(`(?<![\\w-])@${CHART_MENTION_MARKER}:\\d+(?![\\w-])`).test(content);

export const ENTITY_MENTION_REGEX = new RegExp(
  `(?<![\\w-])@(?<type>${[...MENTION_MARKER_TYPES, CHART_MENTION_MARKER].join(
    "|"
  )}):(?<value>\\d+)(?![\\w-])`,
  "g"
);

const WHOLE_MENTION_REGEX = new RegExp(`^${ENTITY_MENTION_REGEX.source}$`);

// The parser keeps a marker it couldn't name as its own text segment.
export const getUnresolvedMentionType = (
  text: string
): MentionMarkerType | typeof CHART_MENTION_MARKER | undefined =>
  WHOLE_MENTION_REGEX.exec(text)?.groups?.type as
    | MentionMarkerType
    | typeof CHART_MENTION_MARKER
    | undefined;

// Matches an in-progress @query at the cursor: @ then up to one space (two
// words) of any non-@ characters, so names with spaces and email-like
// usernames stay searchable.
export const MENTION_TRIGGER_REGEX = /@([^@\s]*(?:\s[^@\s]*)?)$/;

export type ContentSegment =
  | { chartId: number; displayName: string; type: "chart" }
  | { displayName: string; entityId: number; entityType: MentionableEntityType; type: "entity" }
  | { text: string; type: "text" };

// The subset of EntityType that can be @mentioned in a coaching note.
export type MentionableEntityType =
  | EntityType.COMPANY
  | EntityType.COMPANY_GROUP
  | EntityType.DEAL
  | EntityType.DEAL_GROUP
  | EntityType.PEOPLE_GROUP
  | EntityType.PERSON
  | EntityType.ROUTE
  | EntityType.TERRITORY
  | EntityType.USER;

const ENTITY_TYPE_TO_MARKER: Record<MentionableEntityType, MentionMarkerType> = {
  [EntityType.COMPANY]: "account",
  [EntityType.COMPANY_GROUP]: "account-group",
  [EntityType.DEAL]: "deal",
  [EntityType.DEAL_GROUP]: "deal-group",
  [EntityType.PEOPLE_GROUP]: "contact-group",
  [EntityType.PERSON]: "contact",
  [EntityType.ROUTE]: "unified-route",
  [EntityType.TERRITORY]: "territory",
  [EntityType.USER]: "user",
};

const MARKER_TO_ENTITY_TYPE = Object.fromEntries(
  Object.entries(ENTITY_TYPE_TO_MARKER).map(([entityType, marker]) => [marker, entityType])
) as Record<MentionMarkerType, MentionableEntityType>;

export const isMentionableEntityType = (
  entityType: EntityType
): entityType is MentionableEntityType => entityType in ENTITY_TYPE_TO_MARKER;

export const entityTypeToMarker = (entityType: MentionableEntityType): MentionMarkerType =>
  ENTITY_TYPE_TO_MARKER[entityType];

export const markerToEntityType = (marker: MentionMarkerType): MentionableEntityType =>
  MARKER_TO_ENTITY_TYPE[marker];

export const createEntityMentionToken = (
  entityType: MentionableEntityType,
  entityId: number
): string => `@${ENTITY_TYPE_TO_MARKER[entityType]}:${entityId}`;

// Build a lookup key matching the marker's own `type:id` form.
export const mentionKey = (type: MentionMarkerType, entityId: number): string =>
  `${type}:${entityId}`;

const NO_CHART_NAMES: ReadonlyMap<number, string> = new Map();

// Display only — the composer needs its raw text back for editing. Normalizes
// the composer's multi-line gap before a chart pill to one blank line so it
// costs a fixed single line against the 3-line clamp.
export const collapseChartMentionGaps = (segments: ContentSegment[]): ContentSegment[] =>
  segments.map((segment, index) => {
    if (segment.type !== "text") {
      return segment;
    }
    let text = segment.text;
    if (segments[index + 1]?.type === "chart" && /\n\s*$/.test(text)) {
      text = text.replace(/\s+$/, "\n\n");
    }
    if (index === segments.length - 1) {
      text = text.replace(/\s+$/, "");
    }
    return text === segment.text ? segment : { text, type: "text" };
  });

export const parseEntityMentions = (
  content: string,
  mentions: EntityMention[] = [],
  // Chart names never arrive from the server, so they are supplied separately.
  chartNames: ReadonlyMap<number, string> = NO_CHART_NAMES
): ContentSegment[] => {
  const nameByKey = new Map(
    mentions.map(({ displayName, entityId, type }) => [mentionKey(type, entityId), displayName])
  );

  return splitStringByRegExp(content, ENTITY_MENTION_REGEX).map((part): ContentSegment => {
    if (part.type === "text") {
      return { text: part.text, type: "text" };
    }

    const groups = part.match.groups as {
      type: MentionMarkerType | typeof CHART_MENTION_MARKER;
      value: string;
    };
    const entityId = parseInt(groups.value, 10);

    if (groups.type === CHART_MENTION_MARKER) {
      const chartName = chartNames.get(entityId);
      return chartName === undefined
        ? { text: part.match[0], type: "text" }
        : { chartId: entityId, displayName: chartName, type: "chart" };
    }

    const markerType = groups.type;
    const displayName = nameByKey.get(mentionKey(markerType, entityId));

    // Unresolved markers stay text segments so the composer keeps the raw input;
    // EntityMentionContent draws them as a not-available pill.
    return displayName === undefined
      ? { text: part.match[0], type: "text" }
      : { displayName, entityId, entityType: MARKER_TO_ENTITY_TYPE[markerType], type: "entity" };
  });
};
